import { Button, Checkbox, Select, Switch } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UnsavedChangesScopeEnum } from '../../../enums/UnsavedChangesScopeEnum';
import { useRequestLeave } from '../../../hooks/useRequestLeave';
import { useSnapshotDirtyState } from '../../../hooks/useSnapshotDirtyState';
import { createGuardedModalCancelHandler } from '../../../utils/openGuardedFormModal';
import { FileAccessNotificationSettingOperationEnum } from '../../../enums/FileAccessNotificationSettingOperationEnum';
import { FileAccessNotificationSettingTriggerEnum } from '../../../enums/FileAccessNotificationSettingTriggerEnum';
import type { FileAccessNotificationSetting } from '../../../interfaces/FileAccessNotificationSetting';

/**
 * Collects all operation values from the FileAccessNotificationSettingOperationEnum.
 */
const ALL_OPERATIONS = Object.values(FileAccessNotificationSettingOperationEnum);

/**
 * Default file access notification setting: everything disabled. When the user enables the main switch, owner is turned on and recipients stay off.
 */
export const DEFAULT_FILE_ACCESS_NOTIFICATION_SETTING: FileAccessNotificationSetting = {
  enabled: false,
  recursive: true,
  notifyOwner: {
    enabled: false,
    operations: [...ALL_OPERATIONS],
  },
  notifyRecipients: {
    enabled: false,
    trigger: FileAccessNotificationSettingTriggerEnum.Anyone,
    operations: [...ALL_OPERATIONS],
  },
};

/**
 * Provides translation keys for each operation enum value.
 */
const OPERATION_KEYS: Record<FileAccessNotificationSettingOperationEnum, string> = {
  [FileAccessNotificationSettingOperationEnum.Download]:
    'fileAccessNotification.operation.download',
  [FileAccessNotificationSettingOperationEnum.Open]: 'fileAccessNotification.operation.open',
  [FileAccessNotificationSettingOperationEnum.Edit]: 'fileAccessNotification.operation.edit',
  [FileAccessNotificationSettingOperationEnum.Delete]: 'fileAccessNotification.operation.delete',
  [FileAccessNotificationSettingOperationEnum.Rename]: 'fileAccessNotification.operation.rename',
  [FileAccessNotificationSettingOperationEnum.Add]: 'fileAccessNotification.operation.add',
  [FileAccessNotificationSettingOperationEnum.Move]: 'fileAccessNotification.operation.move',
  [FileAccessNotificationSettingOperationEnum.Copy]: 'fileAccessNotification.operation.copy',
};

/**
 * Structure for specifying the override notification target recipient.
 */
export type FileAccessNotificationOverrideTarget = {
  recipientKey: string;
  recipientLabel: string;
};

/**
 * Props accepted by FileAccessNotificationSettingsForm.
 */
export interface FileAccessNotificationSettingsFormProps {
  value: FileAccessNotificationSetting;
  onChange: (value: FileAccessNotificationSetting) => void;
  isFolder?: boolean;
  disabled?: boolean;
  /**
   * If set, the form edits an override for the specified recipient (their label appears in the modal title elsewhere).
   */
  fileAccessNotificationOverrideTarget?: FileAccessNotificationOverrideTarget | null;
  /**
   * If set, acts as a modal: edits a local draft, shows Cancel/Save buttons; Save calls onChange then onClose. Cancel confirms discarding if dirty then onClose.
   */
  onClose?: () => void;
}

/**
 * Shallow equality check for two FileAccessNotificationSetting objects.
 */
function areSettingsEqual(
  a: FileAccessNotificationSetting,
  b: FileAccessNotificationSetting,
): boolean {
  return (
    a.enabled === b.enabled &&
    a.recursive === b.recursive &&
    a.notifyOwner.enabled === b.notifyOwner.enabled &&
    a.notifyOwner.operations.length === b.notifyOwner.operations.length &&
    a.notifyOwner.operations.every((op, i) => b.notifyOwner.operations[i] === op) &&
    a.notifyRecipients.enabled === b.notifyRecipients.enabled &&
    a.notifyRecipients.trigger === b.notifyRecipients.trigger &&
    a.notifyRecipients.operations.length === b.notifyRecipients.operations.length &&
    a.notifyRecipients.operations.every((op, i) => b.notifyRecipients.operations[i] === op)
  );
}

/**
 * FileAccessNotificationSettingsForm
 *
 * Presents a form for configuring file access notification settings.
 * - Handles overall notification settings and per-recipient overrides (modal mode).
 * - Allows toggling owner/recipients notifications, customizing user triggers and file operations.
 */
export const FileAccessNotificationSettingsForm = ({
  value,
  onChange,
  isFolder = false,
  disabled = false,
  fileAccessNotificationOverrideTarget = null,
  onClose,
}: FileAccessNotificationSettingsFormProps) => {
  const { t } = useTranslation();
  const requestLeave = useRequestLeave();
  // True if shown in a modal, false if used inline.
  const isModalMode = onClose != null;
  // Modal mode keeps a local draft, otherwise operates directly on value prop.
  const [draft, setDraft] = useState<FileAccessNotificationSetting>(value);
  // Used for dirty checking in modal mode.
  const initialValueRef = useRef<FileAccessNotificationSetting>(value);

  // Keep draft and initial value in sync with incoming props.
  useEffect(() => {
    setDraft(value);
    initialValueRef.current = value;
  }, [value]);

  // Derived value to show in the form (draft for modal mode, otherwise direct prop)
  const effectiveValue = isModalMode ? draft : value;

  const isDraftDirty = useMemo(
    () => isModalMode && !areSettingsEqual(draft, initialValueRef.current),
    [draft, isModalMode],
  );

  useSnapshotDirtyState({
    enabled: isModalMode,
    isDirty: isDraftDirty,
    onDiscard: () => setDraft(initialValueRef.current),
    scope: UnsavedChangesScopeEnum.Modal,
  });

  /**
   * Applies a patch to effective settings (draft in modal mode, value prop otherwise).
   */
  const setEffective = useCallback(
    (
      patch:
        | Partial<FileAccessNotificationSetting>
        | ((prev: FileAccessNotificationSetting) => FileAccessNotificationSetting),
    ) => {
      if (isModalMode) {
        setDraft((prev) => (typeof patch === 'function' ? patch(prev) : { ...prev, ...patch }));
      } else {
        const next = typeof patch === 'function' ? patch(value) : { ...value, ...patch };
        onChange(next);
      }
    },
    [isModalMode, value, onChange],
  );

  /**
   * Save the current draft value and close the modal (in modal mode).
   */
  const handleSave = useCallback(() => {
    onChange(draft);
    onClose?.();
  }, [draft, onChange, onClose]);

  /**
   * Cancel edits. If form is dirty (unsaved changes exist), ask for user confirmation before closing.
   */
  const handleCancel = createGuardedModalCancelHandler({
    onConfirmed: () => onClose?.(),
    requestLeave,
    resetForm: () => setDraft(initialValueRef.current),
  });

  /**
   * Helper: apply a partial patch to the main notification setting object.
   */
  const update = useCallback(
    (patch: Partial<FileAccessNotificationSetting>) => {
      setEffective((prev) => ({ ...prev, ...patch }));
    },
    [setEffective],
  );

  /**
   * Helper: patch owner notification subsettings.
   */
  const setNotifyOwner = useCallback(
    (patch: Partial<FileAccessNotificationSetting['notifyOwner']>) => {
      setEffective((prev) => ({
        ...prev,
        notifyOwner: { ...prev.notifyOwner, ...patch },
      }));
    },
    [setEffective],
  );

  /**
   * Helper: patch recipient notification subsettings.
   */
  const setNotifyRecipients = useCallback(
    (patch: Partial<FileAccessNotificationSetting['notifyRecipients']>) => {
      setEffective((prev) => ({
        ...prev,
        notifyRecipients: { ...prev.notifyRecipients, ...patch },
      }));
    },
    [setEffective],
  );

  /**
   * Toggles a file operation type in either the owner or recipient operations array.
   * @param section Which settings section ("notifyOwner" or "notifyRecipients") to update.
   * @param op The operation enum value to toggle.
   * @param checked Add this operation if true, remove if false.
   */
  const toggleOperation = useCallback(
    (
      section: 'notifyOwner' | 'notifyRecipients',
      op: FileAccessNotificationSettingOperationEnum,
      checked: boolean,
    ) => {
      const current = effectiveValue[section].operations;
      // Add or remove the operation
      const next = checked ? [...current, op] : current.filter((o) => o !== op);
      if (section === 'notifyOwner') {
        setNotifyOwner({ operations: next });
      } else {
        setNotifyRecipients({ operations: next });
      }
    },
    [effectiveValue, setNotifyOwner, setNotifyRecipients],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Show banner when editing an override for a specific recipient */}
      {fileAccessNotificationOverrideTarget && (
        <p className="text-gray-dark text-sm" role="status">
          {t('fileAccessNotification.customizeFor')}:{' '}
          {fileAccessNotificationOverrideTarget.recipientLabel}
        </p>
      )}
      {/* Main switch to enable/disable notifications for the file/folder */}
      <div className="flex flex-row items-center justify-between gap-2">
        <span className="text-sm font-medium">
          {t('fileAccessNotification.enableActivityNotifications')}
        </span>
        <Switch
          checked={effectiveValue.enabled}
          onChange={(checked) => {
            if (checked) {
              setEffective((prev) => ({
                ...prev,
                enabled: true,
                notifyOwner: {
                  enabled: true,
                  operations: [...ALL_OPERATIONS],
                },
                notifyRecipients: {
                  ...prev.notifyRecipients,
                  enabled: false,
                },
              }));
            } else {
              update({ enabled: false });
            }
          }}
          disabled={disabled}
          aria-checked={effectiveValue.enabled}
        />
      </div>

      {/* Show further options only if notifications are enabled */}
      {effectiveValue.enabled && (
        <>
          {/* Show 'include subfolders' for folders only */}
          {isFolder && (
            <div className="flex flex-row items-center gap-2">
              <Checkbox
                checked={effectiveValue.recursive}
                onChange={(e) => update({ recursive: e.target.checked })}
                disabled={disabled}
              >
                {t('fileAccessNotification.includeSubfolders')}
              </Checkbox>
            </div>
          )}

          {/* Owner notification setting block */}
          <div className="border-gray-medium flex flex-col gap-3 rounded-md border p-3">
            <div className="flex flex-row items-center justify-between gap-2">
              <span className="text-sm font-medium">{t('fileAccessNotification.notifyOwner')}</span>
              <Switch
                checked={effectiveValue.notifyOwner.enabled}
                onChange={(checked) => setNotifyOwner({ enabled: checked })}
                disabled={disabled}
                aria-checked={effectiveValue.notifyOwner.enabled}
              />
            </div>
            {/* Only show owner operation checkboxes if owner notifications are enabled */}
            {effectiveValue.notifyOwner.enabled && (
              <div className="grid grid-cols-2 gap-2 pl-2">
                {ALL_OPERATIONS.map((op) => (
                  <Checkbox
                    key={op}
                    checked={effectiveValue.notifyOwner.operations.includes(op)}
                    onChange={(e) => toggleOperation('notifyOwner', op, e.target.checked)}
                    disabled={disabled}
                  >
                    {t(OPERATION_KEYS[op])}
                  </Checkbox>
                ))}
              </div>
            )}
          </div>

          {/* Recipients notification setting block */}
          <div className="border-gray-medium flex flex-col gap-3 rounded-md border p-3">
            <div className="flex flex-row items-center justify-between gap-2">
              <span className="text-sm font-medium">
                {t('fileAccessNotification.notifyRecipients')}
              </span>
              <Switch
                checked={effectiveValue.notifyRecipients.enabled}
                onChange={(checked) => setNotifyRecipients({ enabled: checked })}
                disabled={disabled}
                aria-checked={effectiveValue.notifyRecipients.enabled}
              />
            </div>
            {/* Show recipient options only if recipient notifications are enabled */}
            {effectiveValue.notifyRecipients.enabled && (
              <>
                {/* Recipient trigger selection (who can trigger notifications) */}
                <div className="flex flex-col gap-1 pl-2">
                  <label className="text-gray-dark text-sm">
                    {t('fileAccessNotification.whoCanTrigger')}
                  </label>
                  <Select
                    value={effectiveValue.notifyRecipients.trigger}
                    onChange={(trigger) => setNotifyRecipients({ trigger })}
                    disabled={disabled}
                    options={[
                      {
                        value: FileAccessNotificationSettingTriggerEnum.OwnerOnly,
                        label: t('fileAccessNotification.trigger.ownerOnly'),
                      },
                      {
                        value: FileAccessNotificationSettingTriggerEnum.Anyone,
                        label: t('fileAccessNotification.trigger.anyone'),
                      },
                    ]}
                    className="w-full"
                  />
                </div>
                {/* Recipient operations selection */}
                <div className="grid grid-cols-2 gap-2 pl-2">
                  {ALL_OPERATIONS.map((op) => (
                    <Checkbox
                      key={op}
                      checked={effectiveValue.notifyRecipients.operations.includes(op)}
                      onChange={(e) => toggleOperation('notifyRecipients', op, e.target.checked)}
                      disabled={disabled}
                    >
                      {t(OPERATION_KEYS[op])}
                    </Checkbox>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* Modal Save/Cancel actions, shown only in modal mode. Save disabled if notifications enabled but neither owner nor recipients are on. */}
      {isModalMode && (
        <div className="border-gray-medium mt-6 flex flex-row justify-end gap-2 border-t pt-4">
          <Button size="middle" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            size="middle"
            type="primary"
            onClick={handleSave}
            disabled={
              disabled ||
              (effectiveValue.enabled &&
                !effectiveValue.notifyOwner.enabled &&
                !effectiveValue.notifyRecipients.enabled)
            }
          >
            {t('common.save')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileAccessNotificationSettingsForm;
